import { Action } from '@reduxjs/toolkit';
import {
  combineEpics,
  StateObservable,
} from 'redux-observable';
import { Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  ignoreElements,
  map,
  pluck,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { EvoPlayer } from '@ezugi/bootstrap/src/views/GameVideo/EvoPlayer/player';
import {
  GAME_RESULT,
  LOADING_PROGRESS,
  NO_MORE_BETS,
  PLACE_YOUR_BETS,
} from '@ezugi/constants';
import { calcDownloadedSize } from '@ezugi/utils';

import { EVENTS } from '../../../constants/postMessage';
import { postMessage } from '../../../util/postMessage';
import {
  appActions,
  authActions,
  configActions,
  errorActions,
  gameActions,
  lobbyActions,
  mediaActions,
  menuActions,
  roundActions,
  socketActions,
} from '../../actions';
import { configActionsFromSlice } from '../../reducers/config';
import {
  clientIdSelector,
  ossAccessRestrictedCodesSelector,
  tableIdSelector,
  totalBetSelector,
  videoSelector,
} from '../../selectors';
import { RootState } from '../../types';

const { socket } = socketActions;
const { game } = gameActions;
const { app } = appActions;
const { auth } = authActions;
const { error: reduxError } = errorActions;
const { volume, autoplaymute } = mediaActions;
const { lobby } = lobbyActions;
const { home } = menuActions;
const { round } = roundActions;

const config = process.env.NODE_ENV !== 'test'
  ? configActions.config : (configActions.config || configActionsFromSlice);

const handleUserEvent = (event: string, state: RootState) => {
  const player = clientIdSelector(state);
  const tableId = tableIdSelector(state);
  postMessage({ event, tableId, player }, false);
};

const roundNMBEpic = (action$: Observable<Action>) => action$.pipe(
  filter(socket.message.match),
  pluck('payload'),
  filter(({ MessageType }) => MessageType === NO_MORE_BETS),
  tap(() => {
    postMessage(
      {
        event: EVENTS.NO_MORE_BETS,
      },
      false
    );
  }),
  ignoreElements()
);

const roundPYBEpic = (action$: Observable<Action>) => action$.pipe(
  filter(socket.message.match),
  pluck('payload'),
  filter(({ MessageType }) => MessageType === PLACE_YOUR_BETS),
  tap(() => {
    postMessage(
      {
        event: EVENTS.PLACE_YOUR_BETS,
      },
      false
    );
  }),
  ignoreElements()
);

const roundGameResultEpic = (action$: Observable<Action>) => action$.pipe(
  filter(socket.message.match),
  pluck('payload'),
  filter(({ MessageType }) => MessageType === GAME_RESULT),
  tap(() => {
    postMessage(
      {
        event: EVENTS.GAME_RESULT,
      },
      false
    );
  }),
  ignoreElements()
);

export const appStartEpic = (action$: Observable<Action>) => action$.pipe(
  filter(app.start.match),
  tap(() => {
    postMessage(
      {
        event: EVENTS.APP_START,
      },
      false
    );
  }),
  ignoreElements()
);

export const authSuccessEpic = (action$: Observable<Action>) => action$.pipe(
  filter(auth.success.match),
  tap(() => {
    postMessage(
      {
        event: EVENTS.AUTH_SUCCESS,
        gameVersion: window.__TAG_VERSION,
      },
      false
    );
  }),
  ignoreElements()
);

const gameStartEpic = (action$: Observable<Action>) => action$.pipe(
  filter(game.start.match),
  tap(() => {
    postMessage(
      {
        event: EVENTS.GAME_START,
      },
      false
    );
  }),
  ignoreElements()
);

const ossErrorsEpic = (
  action$: Observable<Action>,
  state$:StateObservable<RootState>
) => action$.pipe(
  filter(reduxError.set.match),
  first(),
  takeUntil(action$.pipe(filter((app.start.match)))),
  tap(({ payload }) => {
    /*
       * On OSS Games Evolution's loader is removed
       * when app.start action was dispatched in Ezugi games.
       * When an error is set, for example TABLE_IS_CLOSED
       * app.start will never be dispatched resulting in an infinite Evolution loader,
       * because we never remove it.
       * After talking with Evolution, it was decided that a distinction should be made between errors,
       * for "fatal errors" like game not loaded, we will just send a postMessage letting wrapper know
       * that an error occurred (and they will refresh 5 times and then display an error dialog) and for
       * errors like BJSP's table occupied / insufficient funds, we will remove Evo loader and display our error,
       * so the player has an accurate feedback of why he can not join the table. (we will call those, "restricted access errors")
       * In the future, more codes will be added in this config array (right now, product wants to fix BJSP errors asap)
       * see https://ezugi1.atlassian.net/browse/ERD-9762
    */
    const { code, message } = payload;
    const ossDisplayedErrorCodes = ossAccessRestrictedCodesSelector(
      state$.value
    );
    const shouldDisplayErrorInOss = ossDisplayedErrorCodes.includes(code);

    postMessage(
      {
        event: shouldDisplayErrorInOss
          ? EVENTS.ACCESS_RESTRICTED
          : EVENTS.ERROR_SET,
        code,
        reason: message,
      },
      false
    );
  }),
  ignoreElements()
);

type MapVolumePayload = Parameters<typeof volume.set>[0] | Parameters<typeof autoplaymute.set>[0];

// TODO: https://ezugi1.atlassian.net/browse/ERD-15299
declare const window: {
  __TAG_VERSION?: string
} & Window;

function mapVolumePayload(payload: MapVolumePayload, state: RootState): { muted: boolean } | undefined {
  const sanitizedPayload = typeof payload === 'boolean' ? { muted: payload } : { muted: payload.muted };
  const videoSelectorValue = videoSelector(state) as RootState['video'];
  if (Object.keys(videoSelectorValue as {}).length === 0) {
    return undefined;
  }

  const videoWarningMessage = videoSelectorValue.warning && videoSelectorValue.warning.message;

  const isNanoPlayerAutoplayDisabled = [ 'autoplay', 'failed' ].every((el) => videoWarningMessage?.includes(el));
  const isEvoPlayerAutoplayDisabled = EvoPlayer.canPlaySound === false;

  const updatedVolume = isNanoPlayerAutoplayDisabled || isEvoPlayerAutoplayDisabled
    ? { muted: true }
    : sanitizedPayload;

  return updatedVolume;
}

export const soundMuteEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter((a) => volume.set.match(a) || autoplaymute.set.match(a)),
  pluck('payload'),
  map((payload: any) => mapVolumePayload(payload, state$.value)),
  tap((payload) => {
    postMessage({ ...payload, event: EVENTS.VOLUME_SET }, false);
  }),
  ignoreElements()
);

const resourceReportEpic = (action$: Observable<Action>) => action$.pipe(
  filter((a) => config.request.match(a)
    || config.set.match(a)
    || auth.request.match(a)
    || auth.success.match(a)
    || app.start.match(a)
    || game.set.match(a)),
  tap(() => {
    postMessage(
      {
        type: LOADING_PROGRESS,
        data: calcDownloadedSize(false),
      },
      false
    );
  }),
  ignoreElements()
);

const playerJoinedEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter(app.start.match),
  tap(() => handleUserEvent(EVENTS.PLAYER_JOINED_TABLE, state$.value)),
  ignoreElements()
);

const playerLeftEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
) => action$.pipe(
  filter((a) => home.request.match(a) || lobby.navigate.match(a)),
  tap(() => handleUserEvent(EVENTS.PLAYER_LEFT_TABLE, state$.value)),
  ignoreElements()
);

const roundSetEpic = (action$: Observable<Action>) => action$.pipe(
  filter(round.set.match),
  filter(({ payload }) => Boolean(payload.roundId)),
  map(({ payload }) => payload.roundId),
  distinctUntilChanged(),
  tap((roundId) => {
    postMessage(
      {
        event: EVENTS.ROUND_SET,
        roundId,
      },
      false
    );
  }),
  ignoreElements()
);

const totalBetEpic = (_: Observable<Action>, state$: StateObservable<RootState>) => state$.pipe(
  map(totalBetSelector),
  distinctUntilChanged(),
  tap((totalBetValue) => {
    postMessage({ event: EVENTS.SET_TOTAL_BET, totalBetValue });
  }),
  ignoreElements()
);

export default combineEpics(
  roundNMBEpic,
  roundPYBEpic,
  roundGameResultEpic,
  gameStartEpic,
  appStartEpic,
  authSuccessEpic,
  ossErrorsEpic,
  resourceReportEpic,
  playerJoinedEpic,
  playerLeftEpic,
  soundMuteEpic,
  roundSetEpic,
  totalBetEpic
);
